import type { GridCard } from '@Components/BigBrowse/Model/Classes/GridCard';
import { getCard, getGap } from '@Components/BigBrowse/Model/Classes/GridCard';
import type { PlaneSize } from '@Components/BigBrowse/Model/ModelTypes';
import { PlaneTypes } from '@Components/BigBrowse/Model/ModelTypes';
import { get2ColumnsWidth } from '@Components/BigBrowse/Views/DetailViews/MultiSectionView/common/columnCalculator';
import type { ViewData } from '@Components/BigBrowse/Views/DetailViews/MultiSectionView/common/multisection.data';
import {
  getMultiSectionDescriptionCards,
  getMultiSectionHeroDescriptionCard,
} from '@Components/BigBrowse/Views/DetailViews/MultiSectionView/header/common/descriptionCards';
import {
  descriptionTextSize,
  heroDescriptionTextSize,
} from '@Components/BigBrowse/Views/DetailViews/MultiSectionView/header/common/descriptionTextSizes';
import {
  MULTI_SECTION_DESCRIPTION_BODY_TEXT_PROPS_MOBILE,
  MULTI_SECTION_DESCRIPTION_INTERNAL_MARGIN_MOBILE,
  MULTI_SECTION_DESCRIPTION_TITLE_TEXT_PROPS_MOBILE,
  MULTI_SECTION_HERO_BOTTOM_GAP_MOBILE,
  MULTI_SECTION_SHELF_TOP_GAP_MOBILE,
} from '@Components/BigBrowse/Views/DetailViews/MultiSectionView/header/common/header.figma';
import { addHeaderGapsShelf } from '@Components/BigBrowse/Views/DetailViews/MultiSectionView/header/common/headerShelf/addHeaderShelf';
import { addShelfDisplaceGaps } from '@Components/BigBrowse/Views/DetailViews/MultiSectionView/shelf/common/addShelfDisplaceGaps';
import { isEven, isOdd } from '@Utils/miscUtils';
import { pipeAsyncTransformer } from '@Utils/pipeAsync';
import pipeWith from 'ramda/src/pipeWith';

export const addHeaderMobileLayout = pipeWith(pipeAsyncTransformer, [
  addDescriptionMobile,
  addHeroMobile,
  addHeroBottomGap,
  addHeroDescription,
  addHeaderGapsShelf(MULTI_SECTION_SHELF_TOP_GAP_MOBILE),
  addShelfDisplaceGaps(isOdd),
  addShelfDisplaceGaps(isEven),
]) as (viewData: ViewData) => Promise<ViewData>;

async function addHeroMobile(viewData: ViewData): Promise<ViewData> {
  const side = get2ColumnsWidth(viewData);
  const size = { width: side, height: side };
  const heroCard = viewData.modelData?.header?.showHeroMedia
    ? getMultiSectionHeroCardMobile(viewData, size)
    : getGap(size);
  viewData.layoutTable![0].push(heroCard);
  viewData.layoutTable![1].push(
    getGap({ width: viewData.columnWidth!, height: side })
  );

  return viewData;
}

function getMultiSectionHeroCardMobile(
  viewData: ViewData,
  size: PlaneSize,
  dx = 0
): GridCard<PlaneTypes.Card_MultiSection_Hero> {
  const card = getCard<PlaneTypes.Card_MultiSection_Hero>({
    type: PlaneTypes.Card_MultiSection_Hero,
    size,
    blockHover: true,
    data: {
      dx,
      dataModel: viewData.modelData,
      id: ['BrandConcessionHero', viewData.modelData.concessionId].join('_'),
      key: ['BrandConcessionHero', viewData.modelData.concessionId].join('_'),
      imageUrl: viewData.modelData.header.heroImageUrl,
      isViewHero: true,
      type: undefined,
      sectionId: viewData.modelData.concessionId,
    },
  });

  // @ts-expect-error
  card.key = card.data.key;

  return card;
}

const getTextSize = descriptionTextSize(
  MULTI_SECTION_DESCRIPTION_TITLE_TEXT_PROPS_MOBILE,
  MULTI_SECTION_DESCRIPTION_BODY_TEXT_PROPS_MOBILE,
  MULTI_SECTION_DESCRIPTION_INTERNAL_MARGIN_MOBILE
);

const getHeroDescriptionTextSize = heroDescriptionTextSize(
  MULTI_SECTION_DESCRIPTION_TITLE_TEXT_PROPS_MOBILE,
  MULTI_SECTION_DESCRIPTION_BODY_TEXT_PROPS_MOBILE,
  MULTI_SECTION_DESCRIPTION_INTERNAL_MARGIN_MOBILE
);

async function addDescriptionMobile(viewData: ViewData): Promise<ViewData> {
  const size = await getTextSize(viewData);

  viewData.layoutTable![0].push(
    getMultiSectionDescriptionCards({ viewData, size, layout: 'mobile' })
  );
  viewData.layoutTable![1].push(
    getGap({ width: viewData.columnWidth!, height: size.height })
  );

  return viewData;
}

async function addHeroDescription(viewData: ViewData): Promise<ViewData> {
  const size = await getHeroDescriptionTextSize(viewData);

  viewData.layoutTable![0].push(
    getMultiSectionHeroDescriptionCard({ viewData, size, layout: 'mobile' })
  );
  viewData.layoutTable![1].push(
    getGap({ width: viewData.columnWidth!, height: size.height })
  );

  return viewData;
}

async function addHeroBottomGap(viewData: ViewData): Promise<ViewData> {
  viewData.layoutTable?.forEach((column) =>
    column.push(
      getGap({
        width: viewData.columnWidth!,
        height: MULTI_SECTION_HERO_BOTTOM_GAP_MOBILE,
      })
    )
  );

  return viewData;
}
